import { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";

import { useAnalyzePlaybook } from "../hooks/analyze-playbook";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { IRiskCategoryData, MitigationType, setRisks } from "../../store/playbook-analysis-slice";

import Loader from "../components/NewLoader";
import type { RootState } from "src/store/store";
import ErrorComponent from "../../utils/components/ErrorComponent";

export function Analysis() {
	const dispatch = useDispatch();

	const [params, setParams] = useSearchParams();
	const tabParam = params.get("tab") ?? MitigationType.CONTRADICTORY;

	const handleChangeTab = useCallback(
		(value: string) => {
			setParams({ tab: value });
		},
		[setParams]
	);

	const contradictory = useSelector((state: RootState) => state.analysis.contradictory);
	const missing = useSelector((state: RootState) => state.analysis.missing);
	const processed = useSelector((state: RootState) => state.analysis.processed);

	const isDataAvailable = !!contradictory || !!missing || !!processed;

	const { data, isLoading, error } = useAnalyzePlaybook(isDataAvailable);

	useEffect(() => {
		if (data && !isDataAvailable) {
			dispatch(setRisks(data));
		}
	}, [data]);

	if (error) {
		return <ErrorComponent open message={error} />;
	}

	if (isLoading || !isDataAvailable) {
		return <Loader />;
	}

	return (
		<Tabs value={tabParam} onValueChange={handleChangeTab} className="w-full">
			<TabsList className="w-full">
				<TabsTrigger value={MitigationType.CONTRADICTORY}>Risks</TabsTrigger>
				<TabsTrigger value={MitigationType.MISSING}>Missing</TabsTrigger>
			</TabsList>

			<TabsContent value={MitigationType.CONTRADICTORY}>
				{contradictory.riskCount === 0 ? (
					<div className="flex justify-center items-center h-full text-black font-bold text-base ml-1">
						No risks detected in this contract
					</div>
				) : (
					<>
						<div className="rounded-lg px-3 py-0.5 grid w-fit justify-center items-center text-xs mx-2 bg-red-50 text-red-500">
							{contradictory.riskCount + " "}Active Risks
						</div>

						<RiskRows keyTerms={contradictory.risks} type={MitigationType.CONTRADICTORY} />
					</>
				)}
			</TabsContent>

			<TabsContent value={MitigationType.MISSING}>
				{missing.riskCount === 0 ? (
					<div className="flex justify-center items-center h-full text-black font-bold text-base ml-1">
						No missing term detected in this contract
					</div>
				) : (
					<>
						<div className="rounded-lg px-3 py-0.5 grid w-fit justify-center items-center text-xs mx-2 bg-yellow-50 text-yellow-700">
							{missing.riskCount + " "}Missing Clauses
						</div>

						<RiskRows keyTerms={missing.risks} type={MitigationType.MISSING} />
					</>
				)}
			</TabsContent>
		</Tabs>
	);
}

function RiskRows({ keyTerms, type }: { keyTerms: IRiskCategoryData["risks"]; type: MitigationType }) {
	return (
		<div>
			{keyTerms.map((keyTerm, idx) => (
				<Link to={`${type}/${idx}`} key={keyTerm.key}>
					<div className="flex justify-between items-center gap-4 px-2 py-3 border-b border-gray-200 cursor-pointer">
						<div className="text-xs font-semibold">{keyTerm.key.replace(/\s*\(\d+\)$/, "")}</div>
						<div className="size-7 rounded-full font-semibold text-xs grid items-center justify-center bg-red-100 text-red-500 shrink-0">
							{keyTerm.values.length}
						</div>
					</div>
				</Link>
			))}
		</div>
	);
}
